import React from "react";
import {
  Button as MuiButton,
  ButtonProps,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { colors, shadows, borderRadius, typography } from "../../theme/tokens";

/**
 * 改良されたButtonコンポーネント
 * Atlassianのインタラクティブ要素設計に基づく
 */

const StyledButton = styled(MuiButton)(({ theme, variant, size }) => ({
  textTransform: "none",
  borderRadius: borderRadius.base,
  fontWeight: typography.fontWeight.medium,
  transition: "all 0.2s ease-in-out",
  position: "relative",

  // サイズ別のパディング調整
  ...(size === "small" && {
    padding: "6px 16px",
    fontSize: typography.fontSize.sm,
  }),
  ...(size === "medium" && {
    padding: "8px 20px",
    fontSize: typography.fontSize.base,
  }),
  ...(size === "large" && {
    padding: "12px 28px",
    fontSize: typography.fontSize.lg,
  }),

  // フォーカス管理
  "&:focus-visible": {
    outline: `2px solid ${colors.primary[500]}`,
    outlineOffset: "2px",
  },

  // Primary variant
  ...(variant === "contained" && {
    boxShadow: shadows.sm,
    "&:hover": {
      boxShadow: shadows.md,
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0px)",
      boxShadow: shadows.sm,
    },
  }),

  // Secondary variant
  ...(variant === "outlined" && {
    borderWidth: "1px",
    borderColor: colors.neutral[300],
    backgroundColor: colors.neutral[0],
    color: colors.neutral[900],
    "&:hover": {
      borderColor: colors.primary[500],
      backgroundColor: colors.primary[50],
      transform: "translateY(-1px)",
      boxShadow: shadows.sm,
    },
  }),

  // Text variant
  ...(variant === "text" && {
    color: colors.neutral[700],
    "&:hover": {
      backgroundColor: colors.neutral[100],
      color: colors.neutral[900],
      transform: "translateY(-1px)",
    },
  }),

  // Disabled状態
  "&.Mui-disabled": {
    backgroundColor: colors.neutral[100],
    color: colors.neutral[400],
    cursor: "not-allowed",
    transform: "none",
    boxShadow: "none",
    "&:hover": {
      transform: "none",
      boxShadow: "none",
    },
  },
}));

interface CustomButtonProps extends ButtonProps {
  loading?: boolean;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Button: React.FC<CustomButtonProps> = ({
  children,
  loading = false,
  icon,
  disabled,
  onClick,
  ...props
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (loading || disabled) return;
    onClick?.(event);
  };

  return (
    <StyledButton
      disabled={disabled || loading}
      onClick={handleClick}
      startIcon={loading ? undefined : icon}
      {...props}
    >
      {loading ? (
        <>
          <CircularProgress
            size={16}
            sx={{
              mr: 1,
              color: "inherit",
            }}
          />
          読み込み中...
        </>
      ) : (
        children
      )}
    </StyledButton>
  );
};
